import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { saveToken, apiPost } from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Verify2FA() {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [displayCode, setDisplayCode] = useState(null);
  const inputRefs = useRef([]);

  // Get user data from multiple sources (location state, sessionStorage, localStorage)
  const getStoredData = () => {
    // Try sessionStorage first
    const sessionData = sessionStorage.getItem('pending_2fa');
    if (sessionData) {
      try {
        return JSON.parse(sessionData);
      } catch {
        // continue
      }
    }
    // Try localStorage as backup
    const localData = localStorage.getItem('pending_2fa');
    if (localData) {
      try {
        return JSON.parse(localData);
      } catch {
        // continue
      }
    }
    return {};
  };

  const storedData = getStoredData();
  const user_id = location.state?.user_id || storedData.user_id;
  const email = location.state?.email || storedData.email;
  const fromEmailVerification = location.state?.fromEmailVerification || storedData.fromEmailVerification;
  const fallback_code = location.state?.fallback_code || storedData.fallback_code;

  // Store in BOTH sessionStorage and localStorage when we have state data
  useEffect(() => {
    const dataToStore = {
      user_id: location.state?.user_id || storedData.user_id,
      email: location.state?.email || storedData.email,
      fromEmailVerification: location.state?.fromEmailVerification || storedData.fromEmailVerification,
      fallback_code: location.state?.fallback_code || storedData.fallback_code
    };
    
    if (dataToStore.user_id && dataToStore.email) {
      sessionStorage.setItem('pending_2fa', JSON.stringify(dataToStore));
      localStorage.setItem('pending_2fa', JSON.stringify(dataToStore));
    }
    
    // Set display code
    if (dataToStore.fallback_code) {
      setDisplayCode(dataToStore.fallback_code);
    }
  }, [location.state, storedData.user_id, storedData.email, storedData.fromEmailVerification, storedData.fallback_code]);

  useEffect(() => {
    // Redirect to login if no user data available
    if (!user_id || !email) {
      // Clear any stale pending_2fa data
      sessionStorage.removeItem('pending_2fa');
      localStorage.removeItem('pending_2fa');
      navigate('/app/login');
    }
  }, [user_id, email, navigate]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    // Resend cooldown timer
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, '').slice(0, 6);
        if (digits.length > 0) {
          const newCode = [...code];
          for (let i = 0; i < digits.length; i++) {
            newCode[i] = digits[i];
          }
          setCode(newCode);
          if (digits.length === 6) {
            handleVerify(digits);
          } else {
            inputRefs.current[digits.length]?.focus();
          }
        }
      });
    }
  };

  const handleVerify = async (codeToVerify) => {
    const verifyCode = codeToVerify || code.join('');
    
    if (verifyCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiPost('/auth/verify-2fa', {
        user_id,
        code: verifyCode,
        remember_device: rememberDevice,
      });

      const data = await response.json();

      if (response.ok) {
        // Clear stored 2FA data
        sessionStorage.removeItem('pending_2fa');
        // Save token to localStorage for mobile browsers
        if (data.session_token) {
          saveToken(data.session_token);
        }
        // Successfully verified - navigate to dashboard
        navigate('/app', { 
          state: { user: data }, 
          replace: true 
        });
      } else {
        setError(data.detail || 'Invalid verification code');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.');
      console.error('2FA verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;

    setResending(true);
    setError('');

    try {
      const response = await apiPost('/auth/resend-2fa', { user_id });

      if (response.ok) {
        const data = await response.json();
        setResendCooldown(60); // 60 second cooldown
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        
        // Check if email failed and we have a fallback code
        if (data.email_failed && data.fallback_code) {
          // Update sessionStorage with new fallback code
          const stored = sessionStorage.getItem('pending_2fa');
          if (stored) {
            const pendingData = JSON.parse(stored);
            pendingData.fallback_code = data.fallback_code;
            pendingData.email_failed = true;
            sessionStorage.setItem('pending_2fa', JSON.stringify(pendingData));
          }
          setShowFallbackCode(true);
          // Force re-render with new code
          window.location.reload();
        }
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to resend code');
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const maskedEmail = email 
    ? email.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔐</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="2fa-title">
            Two-Factor Authentication
          </h1>
          {(displayCode || fallback_code) ? (
            <>
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-emerald-800 font-medium mb-2">🔑 Your verification code:</p>
                <div className="bg-white border-2 border-emerald-300 rounded-lg p-3">
                  <span className="text-2xl font-bold tracking-widest text-gray-900">{displayCode || fallback_code}</span>
                </div>
                <p className="text-gray-500 text-xs mt-2">We also sent this to your email</p>
              </div>
            </>
          ) : (
            <>
              <p className="text-gray-600 mt-2">
                We sent a verification code to
              </p>
              <p className="font-medium text-gray-900">{maskedEmail}</p>
            </>
          )}
        </div>

        {/* Code Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
            Enter 6-digit code
          </label>
          <div className="flex justify-center gap-2">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                data-testid={`2fa-input-${index}`}
                className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-lg transition-all ${
                  error ? 'border-red-300 bg-red-50' : 'border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200'
                }`}
                disabled={loading}
              />
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Remember Device */}
        <label className="flex items-center gap-3 mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-700">
            Trust this device for 30 days
          </span>
        </label>

        {/* Verify Button */}
        <button
          onClick={() => handleVerify()}
          disabled={loading || code.join('').length !== 6}
          data-testid="verify-2fa-btn"
          className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all ${
            loading || code.join('').length !== 6
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-xl'
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
              Verifying...
            </span>
          ) : (
            'Verify'
          )}
        </button>

        {/* Resend Code */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Didn&apos;t receive the code?
          </p>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || resending}
            className={`text-sm font-medium ${
              resendCooldown > 0 || resending
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-indigo-600 hover:text-indigo-800'
            }`}
          >
            {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
          </button>
        </div>

        {/* Back to Login */}
        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <button
            onClick={() => {
              // Clear pending_2fa to prevent 2FA loop
              sessionStorage.removeItem('pending_2fa');
              navigate('/app/login');
            }}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}

export default Verify2FA;
